import {
  TElementDefaults,
  TElementPreset,
  TElementStyle,
  TElementType,
  TWidgetPreview,
} from './StudioEditorProvider.types';

export const DEFAULT_FONT_FAMILY = 'Inter';

export const CTA_SIDE_MARGIN = 6;

export const HISTORY_LIMIT = 50;

/**
 * `satisfies` rather than a type annotation: the wire requires every style field, and this is
 * where `toWireStyle` reads its fallbacks from. An annotation would widen each field back to
 * `T | undefined` and make those fallbacks useless.
 */
export const BASE_STYLE = {
  color: '#FFFFFF',
  backgroundColor: '#19E3CF',
  fontSize: 18,
  fontFamily: DEFAULT_FONT_FAMILY,
  fontWeight: 500,
  textAlign: 'center',
  verticalAlign: 'center',
  lineHeight: 1.2,
  borderRadius: 0,
  opacity: 1,
} satisfies TElementStyle;

export const COUNTDOWN_DEFAULT_LEAD_MS = 3 * 24 * 60 * 60 * 1000;

export const ELEMENT_DEFAULTS: Record<string, TElementDefaults> = {
  title: {
    x: 15,
    y: 28,
    width: 70,
    height: 10,
    content: 'Add a title',
    style: { fontSize: 26, fontWeight: 700, color: '#FFFFFF', textAlign: 'center' },
  },
  subtitle: {
    x: 15,
    y: 39,
    width: 70,
    height: 7,
    content: 'Add a subtitle',
    style: { fontSize: 20, fontWeight: 500, color: '#FFFFFF', textAlign: 'center' },
  },
  body: {
    x: 15,
    y: 47,
    width: 70,
    height: 6,
    content: 'Add a body text',
    style: { fontSize: 15, fontWeight: 400, color: '#FFFFFF', textAlign: 'center' },
  },
  button: {
    x: CTA_SIDE_MARGIN,
    y: 84,
    width: 100 - 2 * CTA_SIDE_MARGIN,
    height: 6,
    content: 'Button',
    style: {
      fontSize: 15,
      fontWeight: 500,
      color: '#0B0B12',
      backgroundColor: '#19E3CF',
      borderRadius: 24,
      textAlign: 'center',
    },
  },
  'swipe-up': {
    x: 30,
    y: 90,
    width: 40,
    height: 9,
    content: 'Swipe up',
    style: {
      fontSize: 14,
      fontWeight: 600,
      color: '#0B0B12',
      backgroundColor: '#FFFFFF',
      borderRadius: 100,
      textAlign: 'center',
    },
  },
  link: {
    x: 30,
    y: 88,
    width: 40,
    height: 5,
    content: 'Learn more',
    style: {
      fontSize: 14,
      fontWeight: 500,
      color: '#FFFFFF',
      backgroundColor: 'transparent',
      borderRadius: 0,
      textAlign: 'center',
    },
  },
  poll: {
    x: 12,
    y: 36,
    width: 76,
    height: 21,
    question: 'Which do you prefer?',
    options: ['Option A', 'Option B'],
    results: 'off',
    style: {},
  },
  quiz: {
    x: 12,
    y: 34,
    width: 76,
    height: 24,
    // A quiz carries its prompt in `content`, not `question` — see `TStoryQuizElementWire`.
    content: 'Guess the right answer',
    options: ['Option A', 'Option B'],
    correctIndex: 0,
    reveal: '',
    style: {},
  },
  'emoji-slider': {
    x: 15,
    y: 44,
    width: 70,
    height: 13,
    content: 'How do you feel about this?',
    emoji: '😍',
    style: {},
  },
  reaction: {
    x: 12,
    y: 44,
    width: 76,
    height: 13,
    content: 'React to this',
    emojis: ['👍', '🔥', '😍', '😮'],
    style: {},
  },
  countdown: {
    x: 12,
    y: 38,
    width: 76,
    height: 19,
    content: 'Sale ends in',
    countdownStyle: 'boxes',
    endedText: 'This offer has ended',
    style: {},
  },
  rectangle: {
    x: 30,
    y: 42,
    width: 40,
    height: 14,
    style: { backgroundColor: '#FFFFFF', borderRadius: 4 },
  },
  circle: {
    x: 35,
    y: 40,
    width: 30,
    height: 13.5,
    style: { backgroundColor: '#FFFFFF', borderRadius: 999 },
  },
  line: {
    x: 25,
    y: 50,
    width: 50,
    height: 1,
    style: { backgroundColor: '#FFFFFF', borderRadius: 2 },
  },
  emoji: {
    x: 40,
    y: 44,
    width: 21,
    height: 9.5,
    content: '😀',
    // Base-canvas px, and the size the default box renders at: min(21% × 360, 9.5% × 800).
    style: { fontSize: 75, backgroundColor: 'transparent', borderRadius: 0 },
  },
  image: {
    x: 25,
    y: 28,
    width: 50,
    height: 24,
    style: { backgroundColor: '#2F2F44', borderRadius: 8 },
  },
  video: {
    x: 25,
    y: 28,
    width: 50,
    height: 24,
    style: { backgroundColor: '#2F2F44', borderRadius: 8 },
  },
  // Forward-compatible fallback for a type this build does not know.
  '*': {
    x: 18,
    y: 40,
    width: 64,
    height: 18,
    content: 'Interactive',
    style: {
      fontSize: 16,
      fontWeight: 500,
      color: '#FFFFFF',
      backgroundColor: 'rgba(255,255,255,0.14)',
      borderRadius: 12,
      textAlign: 'center',
    },
  },
};

/**
 * Which type an insert preset produces. Presets that share a type differ only in their starting
 * geometry and style, which is why the preset itself is never stored on the element.
 */
export const PRESET_TYPE: Record<TElementPreset, TElementType> = {
  title: 'text',
  subtitle: 'text',
  body: 'text',
  rectangle: 'shape',
  circle: 'shape',
  line: 'shape',
  button: 'button',
  'swipe-up': 'swipe-up',
  link: 'link',
  poll: 'poll',
  quiz: 'quiz',
  'emoji-slider': 'emoji-slider',
  reaction: 'reaction',
  countdown: 'countdown',
  image: 'image',
  video: 'video',
  emoji: 'emoji',
};

/**
 * Grouping predicates, replacing the old `type` column. They are the only place the five old
 * categories still exist, and they exist for the UI alone — nothing is stored.
 */
const WIDGET_TYPES: TElementType[] = ['poll', 'quiz', 'emoji-slider', 'reaction', 'countdown'];
const CTA_TYPES: TElementType[] = ['button', 'swipe-up', 'link'];
const MEDIA_TYPES: TElementType[] = ['image', 'video'];

export const isWidgetType = (type: TElementType): boolean => WIDGET_TYPES.includes(type);
export const isCtaType = (type: TElementType): boolean => CTA_TYPES.includes(type);
export const isMediaType = (type: TElementType): boolean => MEDIA_TYPES.includes(type);

/** Text presets keep their role only long enough to pick a theme size at creation. */
export const TEXT_ROLES = new Set(['title', 'subtitle', 'body']);

export const TYPOGRAPHIC_FIELDS = [
  'fontSize',
  'fontFamily',
  'fontWeight',
  'textAlign',
  'verticalAlign',
  'lineHeight',
] as const;

export const DEFAULT_WIDGET_PREVIEW: TWidgetPreview = { answered: false, index: 0, value: 0.5 };
